import axios, { type AxiosInstance, AxiosRequestConfig } from 'axios'
import { useRequest } from 'ahooks'
import Cookie from 'js-cookie'
import type { Options, Plugin, Result } from 'ahooks/lib/useRequest/src/types'

const COOKIE_KEY = 'Authorization'
const LOCAL_STORAGE_KEY = 'Authorization'
export type AuthService<T, TParams extends any[]> = (axios: AxiosInstance, ...params: TParams) => Promise<T>
export type AxiosOptions<T, TParams extends any[]> = Options<T, TParams> & {
  axiosOptions?: AxiosRequestConfig
}
export class ServerError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.code = code
  }
}
const token = localStorage.getItem(LOCAL_STORAGE_KEY) || Cookie.get(COOKIE_KEY)
const authAxios = axios.create({
  headers: {
    Authorization: token as string,
  },
})
authAxios.interceptors.response.use(
  (res) => {
    // 返回200，但是success为false，说明是业务错误，直接reject
    if (res.data.success === false) {
      return Promise.reject(new ServerError(res.data.errorDesc, res.data.errorCode))
    }
    // 这一步是为了统一后端接口返回值硬编码，后端接口返回的数据结构不统一，有的是带result，有的是直接返回
    res.data = res.data.result ?? res.data
    return res
  },
  (err) => {
    // 401或者403，说明token失效，需跳转到过期页面，PS:不要直接删除本地token
    if (err.response?.status === 401 || err.response?.status === 403) {
      window.location.href = `/expired#${window.location.pathname + window.location.search}`
    }
    return Promise.reject(err)
  }
)

/**
 * useAxios 是对 ahooks useRequest的封装，主要是为了在axios请求头中添加token
 * @example
 *
 * ```js
  const { loading, data }  = useAxios(async (axios, params) => {
    const response = await axios.get('/api/user', { params })
    return response.data
  },{
    defaultParams: { name: 'test' },
  })
 * ```
 * 
 * @param action 
 * @param options 
 * @param plugins 
 * @returns 
 */
export function useAxios<T, TParams extends any[]>(
  action: AuthService<T, TParams>,
  options?: Options<T, TParams>,
  plugins?: Plugin<T, TParams>[]
): Result<T, TParams> {
  return useRequest((...args: TParams) => action(authAxios, ...args), options, plugins)
}
useAxios.instance = authAxios
useAxios.token = token
useAxios.logout = () => {
  authAxios.get('/backend-api/sso-auth/auth/logout')
  Cookie.remove(COOKIE_KEY)
  localStorage.removeItem(LOCAL_STORAGE_KEY)
  window.location.href = '/login'
}
